import {
	Button,
	CardActions,
	CardContent,
	Grid,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import { useNavigate } from 'react-router-dom';
import { FormType } from '@/api/enum/formType.enum';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import AppRichTextEditor from '@/common/components/editor/AppRichTextEditor';
import {
	useCreateInformationMutation,
	useUpdateInformationMutation,
} from '@/api/information/informationApiSlice';
import {
	Information330Overview,
	InformationDetailRequest,
} from '@/api/DTO/information/information.interface';

interface Props {
	type: FormType;
	details?: Information330Overview;
	setType: Dispatch<SetStateAction<FormType>>;
}

export default function InformationInfoForm({ type, setType, details }: Props) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [needConfirm, setNeedConfirm] = useState(false);
	const [currentTab, setCurrentTab] = useState('zh');
	const [createInformation] = useCreateInformationMutation();
	const [updateInformation] = useUpdateInformationMutation();

	const defaultValues: DefaultValues<InformationDetailRequest> = {
		titleEn: details?.titleEn || '',
		titleZh: details?.titleZh || '',
		subtitleEn: details?.subtitleEn || '',
		subtitleZh: details?.subtitleZh || '',
		contentEn: details?.contentEn || '',
		contentZh: details?.contentZh || '',
	};

	const formSchema = Yup.object().shape({
		titleZh: Yup.string().required(t('errorMessage.required')),
		contentZh: Yup.string().required(t('errorMessage.required')),
	});
	const methods = useForm<InformationDetailRequest>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	useEffect(() => {
		const subscription = methods.watch((value) => {
			if (!needConfirm) setNeedConfirm(true);
		});

		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const onSubmit: SubmitHandler<InformationDetailRequest> = async (data) => {
		const obj = { ...data };
		const response =
			type == FormType.create
				? await createInformation(obj)
				: await updateInformation({
						...obj,
						id: details?.id || '',
				  });

		if ('data' in response) {
			enqueueSnackbar(
				type == FormType.create
					? t('snackbar.createInformationSuccess')
					: t('snackbar.editInformationSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			if (type == FormType.create) {
				navigate('/information-manage/information-list');
			} else {
				setType(FormType.view);
			}
		}
	};

	const handleClose = () => {
		if (type == FormType.edit) return setType(FormType.view);
		navigate('/information-manage/information-list');
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<CardContent sx={{ padding: '24px' }}>
						<Tabs value={currentTab} onChange={handleTabChange}>
							<Tab
								label={
									<Typography variant='body1'>{t('information.zh')}</Typography>
								}
								value='zh'
							/>
							<Tab
								label={
									<Typography variant='body1'>{t('information.en')}</Typography>
								}
								value='en'
							/>
						</Tabs>
						{currentTab == 'zh' && (
							<Grid container spacing={3} marginTop={0} marginBottom={3}>
								<Grid item xs={6}>
									<AppTextField
										control={methods.control}
										name='titleZh'
										label={t('information.informationTitle')}
										maxLength={20}
									/>
								</Grid>
								<Grid item xs={6}>
									<AppTextField
										control={methods.control}
										name='subtitleZh'
										label={t('information.informationSubTitle_')}
										maxLength={100}
									/>
								</Grid>
								<Grid item xs={12}>
									<Typography variant='body2' color='common.neutral40'>
										{t('information.content_')}
									</Typography>
									<AppRichTextEditor
										control={methods.control}
										name='contentZh'
									/>
								</Grid>
							</Grid>
						)}
						{currentTab == 'en' && (
							<Grid container spacing={3} marginTop={0} marginBottom={3}>
								<Grid item xs={6}>
									<AppTextField
										control={methods.control}
										name='titleEn'
										label={t('information.informationTitle_')}
										maxLength={100}
									/>
								</Grid>
								<Grid item xs={6}>
									<AppTextField
										control={methods.control}
										name='subtitleEn'
										label={t('information.informationSubTitle_')}
										maxLength={500}
									/>
								</Grid>
								<Grid item xs={12}>
									<Typography variant='body2' color='common.neutral40'>
										{t('information.content_')}
									</Typography>
									<AppRichTextEditor
										control={methods.control}
										name='contentEn'
									/>
								</Grid>
							</Grid>
						)}
					</CardContent>
					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button onClick={handleClose}>{t('button.cancel')}</Button>
						<Button
							type='submit'
							variant='contained'
							disabled={!needConfirm && type === FormType.create}
						>
							{type === FormType.edit ? t('button.save') : t('button.create')}
						</Button>
					</CardActions>
				</form>
			</FormProvider>
		</>
	);
}
